// Büyük "Talep nasıl oluşturulur?" gösterimi: gerçek ekran görüntüleri üzerinde el, vurgu ve açıklama balonu
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { ChevronLeft, ChevronRight, Pause, Play, Pointer, RotateCcw, Plus, CheckCircle2 } from 'lucide-react';
import { HOWTO_STEPS, IMG_W, IMG_H, SECTIONS } from './howtoSteps';

const BASE_MS = 3000;
const SPEEDS = [0.5, 1, 1.5, 2, 3];
const SPEED_KEY = 'howto-speed';

const useFitScale = (ref) => {
  const [scale, setScale] = useState(0);
  useLayoutEffect(() => {
    const ro = new ResizeObserver(([e]) => setScale(Math.min(e.contentRect.width / IMG_W, e.contentRect.height / IMG_H)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return scale;
};

const center = ([x, y, w, h]) => [x + Math.min(w / 2, 60), y + h / 2];

const Balloon = ({ step, refEl }) => {
  const [x, y, w, h] = step.rect;
  const below = y + h / 2 < IMG_H * 0.5;
  const left = Math.max(16, Math.min(x, IMG_W - 556));
  return (
    <div ref={refEl} className="absolute z-30 hidden w-[540px] sm:block rounded-2xl bg-slate-900 px-6 py-5 text-white opacity-0 shadow-2xl"
      style={step.balloon || { left, top: below ? y + h + 18 : undefined, bottom: below ? undefined : IMG_H - y + 18 }} data-testid="howto-balloon">
      <p className="text-[28px] font-bold leading-tight text-orange-300">{step.title}</p>
      <p className="mt-2 text-[22px] leading-snug text-slate-100">{step.text}</p>
    </div>
  );
};

const Stage = ({ steps, index, paused, speed, onDone }) => {
  const wrap = useRef(null), stage = useRef(null), hand = useRef(null), ring = useRef(null), balloon = useRef(null), img = useRef(null);
  const prevPos = useRef([IMG_W / 2, IMG_H - 80]);
  const scale = useFitScale(wrap);
  const step = steps[index];
  useEffect(() => {
    const [tx, ty] = center(step.rect);
    const [px, py] = prevPos.current;
    prevPos.current = [tx, ty];
    const opts = { duration: BASE_MS, fill: 'forwards', easing: 'ease-in-out' };
    const t = (x, y, s = 1) => `translate(${x - 8}px, ${y - 4}px) scale(${s})`;
    const anims = [
      img.current.animate([{ opacity: 0.35 }, { opacity: 1, offset: 0.1 }, { opacity: 1 }], opts),
      hand.current.animate(step.tap
        ? [{ transform: t(px, py), opacity: 1 }, { transform: t(tx, ty), offset: 0.28 }, { transform: t(tx, ty, 0.78), offset: 0.34 }, { transform: t(tx, ty), offset: 0.4 }, { transform: t(tx, ty), opacity: 1 }]
        : [{ transform: t(px, py), opacity: 1 }, { transform: t(tx, ty), offset: 0.3 }, { transform: t(tx, ty), opacity: 0.25, offset: 0.45 }, { transform: t(tx, ty), opacity: 0.25 }], opts),
      ring.current.animate([{ opacity: 0, transform: 'scale(1.08)' }, { opacity: 0, transform: 'scale(1.08)', offset: 0.26 }, { opacity: 1, transform: 'scale(1)', offset: 0.34 }, { opacity: 1, transform: 'scale(1)' }], opts),
      balloon.current.animate([{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 0, transform: 'translateY(8px)', offset: 0.32 }, { opacity: 1, transform: 'translateY(0)', offset: 0.42 }, { opacity: 1, transform: 'translateY(0)' }], opts),
    ];
    anims.forEach(a => { a.playbackRate = speed; if (paused) a.pause(); });
    anims[1].onfinish = onDone;
    return () => anims.forEach(a => a.cancel());
  }, [index]);
  useEffect(() => { stage.current?.getAnimations({ subtree: true }).forEach(a => { a.playbackRate = speed; paused ? a.pause() : a.play(); }); }, [paused, speed]);
  const [x, y, w, h] = step.rect;
  return (
    <div ref={wrap} className="flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-xl bg-slate-200/70 p-2 sm:p-3">
      <div style={{ width: IMG_W * scale, height: IMG_H * scale }}>
        <div ref={stage} className="relative origin-top-left select-none overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-slate-300" style={{ width: IMG_W, height: IMG_H, transform: `scale(${scale})` }} aria-hidden="true" data-testid="howto-stage">
          <img ref={img} src={step.src} alt="" className="absolute inset-0 h-full w-full" draggable={false} />
          <div ref={ring} className="absolute z-20 rounded-xl opacity-0 ring-4 ring-orange-500 ring-offset-2" style={{ left: x - 6, top: y - 6, width: w + 12, height: h + 12, boxShadow: '0 0 0 9999px rgba(15,23,42,.28)' }} />
          <Balloon step={step} refEl={balloon} />
          <Pointer ref={hand} className="pointer-events-none absolute left-0 top-0 z-40 h-16 w-16 fill-white text-slate-900 drop-shadow-lg" />
        </div>
      </div>
    </div>
  );
};

const FinishPanel = ({ onReplay, onStart, onClose, buttonLabel, startLabel }) => (
  <div className="absolute inset-0 z-50 flex items-center justify-center rounded-xl bg-slate-900/55 p-4 backdrop-blur-sm" data-testid="howto-finish">
    <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-2xl">
      <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
      <p className="mt-3 text-lg font-bold text-slate-900">Hazırsınız!</p>
      <p className="mt-1 text-sm text-slate-600">Anlatımı istediğiniz zaman <b>"{buttonLabel}"</b> düğmesinden tekrar izleyebilirsiniz.</p>
      <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-center">
        <Button variant="outline" onClick={onReplay} data-testid="howto-replay"><RotateCcw className="mr-1.5 h-4 w-4" />Tekrar İzle</Button>
        {onStart
          ? <Button onClick={onStart} className="bg-orange-500 hover:bg-orange-600" data-testid="howto-start"><Plus className="mr-1.5 h-4 w-4" />{startLabel}</Button>
          : <Button onClick={onClose} className="bg-slate-900 hover:bg-slate-800" data-testid="howto-close">Kapat</Button>}
      </div>
    </div>
  </div>
);

const Controls = ({ go, index, paused, setPaused, speed, setSpeed }) => (
  <div className="flex flex-wrap items-center justify-center gap-2">
    <Button variant="outline" onClick={() => go(index - 1)} data-testid="howto-prev"><ChevronLeft className="mr-1 h-4 w-4" />Önceki</Button>
    <Button variant="outline" onClick={() => setPaused(p => !p)} className="min-w-[110px]" data-testid="howto-pause">
      {paused ? <><Play className="mr-1 h-4 w-4" />Devam</> : <><Pause className="mr-1 h-4 w-4" />Duraklat</>}
    </Button>
    <Button variant="outline" onClick={() => go(index + 1)} data-testid="howto-next">Sonraki<ChevronRight className="ml-1 h-4 w-4" /></Button>
    <div className="ml-1 flex rounded-lg bg-slate-100 p-1" data-testid="howto-speed">
      {SPEEDS.map(s => (
        <button key={s} type="button" onClick={() => setSpeed(s)} aria-pressed={speed === s} data-testid={`howto-speed-${s}`}
          className={`rounded-md px-2.5 py-1 text-xs transition-colors ${speed === s ? 'bg-orange-500 font-semibold text-white shadow-sm' : 'text-slate-600 hover:bg-white'}`}>x{String(s).replace('.', ',')}</button>
      ))}
    </div>
  </div>
);

export const PermitHowtoDialog = ({ open, onClose, onStart, steps = HOWTO_STEPS, sections = SECTIONS, title = 'Çalışma izni talebi nasıl oluşturulur?',
  description = '"Çalışma İzni Ekle" ile açılan sayfa 5 bölümden oluşur; bölümler alt alta sırayla açılır.', buttonLabel = 'Nasıl talep oluşturulur?', startLabel = 'Talep Oluşturmaya Başla' }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [finished, setFinished] = useState(false);
  const [speed, setSpeedState] = useState(() => Number(localStorage.getItem(SPEED_KEY)) || 1);
  const setSpeed = (s) => { localStorage.setItem(SPEED_KEY, String(s)); setSpeedState(s); };
  useEffect(() => { if (open) { setIndex(0); setPaused(false); setFinished(false); } }, [open]);
  // Son adımdan sonra başa dönmez; bitiş paneli açılır
  const go = (i) => {
    if (i >= steps.length) { setFinished(true); return; }
    setFinished(false); setIndex(Math.max(0, i)); setPaused(false);
  };
  const step = steps[index];
  const inSection = steps.filter(x => x.section === step.section);
  const total = sections.length - 1;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex h-[100dvh] w-screen max-w-none flex-col gap-3 rounded-none p-3 sm:h-[94vh] sm:w-[80vw] sm:max-w-[80vw] sm:rounded-xl sm:p-5" data-testid="permit-howto-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="text-xl">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="relative flex min-h-0 flex-1">
          {open && <Stage key={title} steps={steps} index={index} paused={paused} speed={speed} onDone={() => go(index + 1)} />}
          {finished && <FinishPanel onReplay={() => go(0)} onStart={onStart} onClose={onClose} buttonLabel={buttonLabel} startLabel={startLabel} />}
        </div>
        <p className="text-center text-sm text-slate-700 sm:hidden" data-testid="howto-caption-mobile"><b className="text-orange-600">{step.title}:</b> {step.text}</p>
        <p className="text-center text-sm font-semibold text-slate-700 sm:text-base" data-testid="howto-progress">
          {step.section < total ? <>Bölüm {step.section + 1}/{total} · {sections[step.section]} <span className="mx-1 text-slate-300">|</span> Adım {inSection.indexOf(step) + 1}/{inSection.length}</> : 'Son adım · Tekrar izleme'}
        </p>
        <Controls go={go} index={index} paused={paused} setPaused={setPaused} speed={speed} setSpeed={setSpeed} />
      </DialogContent>
    </Dialog>
  );
};
